import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { EntityModal } from './EntityModal';

export type MessageDialogVariant = 'info' | 'success' | 'warning' | 'danger' | 'error';
export type MessageDialogKind = 'alert' | 'confirm' | 'prompt';

export interface MessageDialogProps {
  open: boolean;
  /** 'alert' shows a single OK button, 'confirm' adds Cancel, 'prompt' adds a text input */
  kind: MessageDialogKind;
  /** Drives the icon + accent color (default: 'danger' for confirm, 'info' otherwise) */
  variant?: MessageDialogVariant;
  title?: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Prompt-only: input placeholder */
  placeholder?: string;
  /** Prompt-only: initial input value */
  defaultValue?: string;
  /** Prompt-only: input type (default 'text') */
  inputType?: string;
  /** Called with the input value for prompts, undefined otherwise */
  onConfirm: (value?: string) => void;
  onCancel: () => void;
}

const VARIANT_CONFIG: Record<
  MessageDialogVariant,
  { icon: typeof Info; iconClass: string; iconBg: string; confirmClass: string; title: string }
> = {
  info: {
    icon: Info,
    iconClass: 'text-blue-600',
    iconBg: 'bg-blue-50',
    confirmClass: 'bg-primary-600 hover:bg-primary-700',
    title: 'Notice',
  },
  success: {
    icon: CheckCircle2,
    iconClass: 'text-green-600',
    iconBg: 'bg-green-50',
    confirmClass: 'bg-primary-600 hover:bg-primary-700',
    title: 'Success',
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-amber-600',
    iconBg: 'bg-amber-50',
    confirmClass: 'bg-primary-600 hover:bg-primary-700',
    title: 'Warning',
  },
  danger: {
    icon: AlertTriangle,
    iconClass: 'text-red-600',
    iconBg: 'bg-red-50',
    confirmClass: 'bg-red-600 hover:bg-red-700',
    title: 'Please Confirm',
  },
  error: {
    icon: XCircle,
    iconClass: 'text-red-600',
    iconBg: 'bg-red-50',
    confirmClass: 'bg-primary-600 hover:bg-primary-700',
    title: 'Error',
  },
};

/**
 * In-app replacement for window.confirm / window.alert / window.prompt.
 * Usually driven through `useDialog()` (DialogContext) rather than rendered directly.
 */
export function MessageDialog({
  open,
  kind,
  variant = kind === 'confirm' ? 'danger' : 'info',
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  placeholder,
  defaultValue = '',
  inputType = 'text',
  onConfirm,
  onCancel,
}: MessageDialogProps) {
  const [inputValue, setInputValue] = useState(defaultValue);

  // Re-seed the input each time the dialog opens
  useEffect(() => {
    if (open) setInputValue(defaultValue);
  }, [open, defaultValue]);

  const config = VARIANT_CONFIG[variant];
  const IconComponent = config.icon;
  const resolvedConfirmLabel = confirmLabel ?? (kind === 'alert' ? 'OK' : 'Confirm');

  const handleConfirm = () => {
    onConfirm(kind === 'prompt' ? inputValue : undefined);
  };

  return (
    <EntityModal
      open={open}
      onClose={onCancel}
      title={title ?? config.title}
      size="xs"
      presentation="centered"
      overlayClassName="z-[60]"
      footer={
        <>
          {kind !== 'alert' && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
              data-testid="dialog-cancel"
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            autoFocus={kind !== 'prompt'}
            onClick={handleConfirm}
            className={`px-4 py-2 text-sm text-white rounded-lg ${config.confirmClass}`}
            data-testid="dialog-confirm"
          >
            {resolvedConfirmLabel}
          </button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-full shrink-0 ${config.iconBg}`}>
          <IconComponent size={20} className={config.iconClass} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1 pt-1.5">
          {message && (
            <div className="text-sm text-gray-700 whitespace-pre-line break-words">{message}</div>
          )}
          {kind === 'prompt' && (
            <form
              className={message ? 'mt-3' : ''}
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
            >
              <input
                autoFocus
                type={inputType}
                value={inputValue}
                placeholder={placeholder}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                data-testid="dialog-input"
              />
            </form>
          )}
        </div>
      </div>
    </EntityModal>
  );
}
